import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import type { UserRole } from './types';
import { AppLayout } from './components/layout/AppLayout';
import { Login } from './pages/Login';
import { AuthorDashboard } from './pages/AuthorDashboard';
import { LessonEditor } from './pages/LessonEditor';
import { LessonReview } from './pages/LessonReview';
import { BrowseLessons } from './pages/BrowseLessons';
import { ContentManagerDashboard } from './pages/ContentManagerDashboard';
import { AdminDashboard } from './pages/AdminDashboard';
import { PromptManagement } from './pages/PromptManagement';

interface PrivateRouteProps {
  children: React.ReactElement;
  roles?: UserRole[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, roles }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const RootRedirect: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

const App: React.FC = () => {
  return (
    <Routes>
      {/* Public route */}
      <Route path="/login" element={<Login />} />

      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Protected routes under AppLayout */}
      <Route
        element={
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        }
      >
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <AuthorDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/lessons/new"
          element={
            <PrivateRoute roles={['admin', 'content_manager', 'author']}>
              <LessonEditor />
            </PrivateRoute>
          }
        />

        <Route
          path="/lessons/:id/edit"
          element={
            <PrivateRoute roles={['admin', 'content_manager', 'author']}>
              <LessonEditor />
            </PrivateRoute>
          }
        />

        <Route
          path="/lessons/:id/review"
          element={
            <PrivateRoute roles={['admin', 'content_manager']}>
              <LessonReview />
            </PrivateRoute>
          }
        />

        <Route
          path="/browse"
          element={
            <PrivateRoute>
              <BrowseLessons />
            </PrivateRoute>
          }
        />

        <Route
          path="/reviews"
          element={
            <PrivateRoute roles={['admin', 'content_manager']}>
              <ContentManagerDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute roles={['admin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/prompts"
          element={
            <PrivateRoute roles={['admin']}>
              <PromptManagement />
            </PrivateRoute>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
